/**
 * Bangladesh national EPI routine immunization schedule.
 *
 * Source of truth for every age/interval rule the capture layer enforces.
 * Pure functions only — no I/O — so the rules can be unit tested and reused
 * unchanged on the client for optimistic validation.
 */

export type Antigen =
  | 'BCG' | 'OPV-1' | 'OPV-2' | 'OPV-3' | 'Penta-1' | 'Penta-2' | 'Penta-3'
  | 'PCV-1' | 'PCV-2' | 'PCV-3' | 'IPV-1' | 'IPV-2' | 'MR-1' | 'MR-2';

export interface ScheduleEntry {
  key: Antigen;
  label: string;
  labelBn: string;
  /** Minimum age in days at which the dose may be given. */
  dueDays: number;
  /** Multi-dose series this antigen belongs to, if any. */
  series: 'OPV' | 'Penta' | 'PCV' | 'IPV' | 'MR' | null;
  /** Position within the series. */
  n: number;
  /**
   * The visit column on the paper card (১ম বার … ৬ষ্ঠ বার) this dose is planned
   * for. The card records doses in a grid — vaccine rows against visit columns —
   * so this is how a date written in a cell becomes a named dose.
   */
  visit: 1 | 2 | 3 | 4 | 5 | 6;
}

/*
 * As printed on the national EPI child card (revised December 2016) and its
 * six planned visits:
 *   ১ম বার  BCG, OPV-1, Penta-1, PCV-1 (+ fIPV-1)   6 weeks (BCG as soon after birth as possible)
 *   ২য় বার  OPV-2, Penta-2, PCV-2                   10 weeks
 *   ৩য় বার  OPV-3, Penta-3, IPV                     14 weeks
 *   ৪র্থ বার PCV-3                                  18 weeks — "৩য় ডোজ ১৮ সপ্তাহ বয়সে"
 *   ৫ম বার  MR-1                                    9 months (270 days)
 *   ৬ষ্ঠ বার MR-2                                   15 months
 * IPV is two fractional doses (fIPV) at 6 and 14 weeks, as filled in on cards
 * in use today; the card's IPV row carries both.
 */
export const SCHEDULE: readonly ScheduleEntry[] = [
  { key: 'BCG',     label: 'BCG',     labelBn: 'বিসিজি',       dueDays: 0,   series: null,    n: 1, visit: 1 },
  { key: 'Penta-1', label: 'Penta-1', labelBn: 'পেন্টা-১',     dueDays: 42,  series: 'Penta', n: 1, visit: 1 },
  { key: 'OPV-1',   label: 'OPV-1',   labelBn: 'ওপিভি-১',      dueDays: 42,  series: 'OPV',   n: 1, visit: 1 },
  { key: 'PCV-1',   label: 'PCV-1',   labelBn: 'পিসিভি-১',     dueDays: 42,  series: 'PCV',   n: 1, visit: 1 },
  { key: 'IPV-1',   label: 'fIPV-1',  labelBn: 'আইপিভি-১',     dueDays: 42,  series: 'IPV',   n: 1, visit: 1 },
  { key: 'Penta-2', label: 'Penta-2', labelBn: 'পেন্টা-২',     dueDays: 70,  series: 'Penta', n: 2, visit: 2 },
  { key: 'OPV-2',   label: 'OPV-2',   labelBn: 'ওপিভি-২',      dueDays: 70,  series: 'OPV',   n: 2, visit: 2 },
  { key: 'PCV-2',   label: 'PCV-2',   labelBn: 'পিসিভি-২',     dueDays: 70,  series: 'PCV',   n: 2, visit: 2 },
  { key: 'Penta-3', label: 'Penta-3', labelBn: 'পেন্টা-৩',     dueDays: 98,  series: 'Penta', n: 3, visit: 3 },
  { key: 'OPV-3',   label: 'OPV-3',   labelBn: 'ওপিভি-৩',      dueDays: 98,  series: 'OPV',   n: 3, visit: 3 },
  { key: 'IPV-2',   label: 'fIPV-2',  labelBn: 'আইপিভি-২',     dueDays: 98,  series: 'IPV',   n: 2, visit: 3 },
  { key: 'PCV-3',   label: 'PCV-3',   labelBn: 'পিসিভি-৩',     dueDays: 126, series: 'PCV',   n: 3, visit: 4 },
  { key: 'MR-1',    label: 'MR-1',    labelBn: 'এমআর-১',       dueDays: 270, series: 'MR',    n: 1, visit: 5 },
  { key: 'MR-2',    label: 'MR-2',    labelBn: 'এমআর-২',       dueDays: 450, series: 'MR',    n: 2, visit: 6 }
];

/** The card's dose grid: one row per vaccine, in the order the card prints them. */
export const CARD_ROWS: { row: string; label: string; labelBn: string; antigens: Antigen[] }[] = [
  { row: 'BCG',   label: 'BCG',                        labelBn: 'বিসিজি',                      antigens: ['BCG'] },
  { row: 'Penta', label: 'Penta (DPT, Hep-B, Hib)',    labelBn: 'পেন্টা (ডিপিটি, হেপ-বি, হিব)', antigens: ['Penta-1', 'Penta-2', 'Penta-3'] },
  { row: 'OPV',   label: 'OPV',                        labelBn: 'ওপিভি',                       antigens: ['OPV-1', 'OPV-2', 'OPV-3'] },
  { row: 'PCV',   label: 'PCV',                        labelBn: 'পিসিভি',                      antigens: ['PCV-1', 'PCV-2', 'PCV-3'] },
  { row: 'IPV',   label: 'IPV',                        labelBn: 'আইপিভি',                      antigens: ['IPV-1', 'IPV-2'] },
  { row: 'MR',    label: 'MR (measles & rubella)',     labelBn: 'এমআর (হাম ও রুবেলা)',          antigens: ['MR-1', 'MR-2'] }
];

/** The six planned visits, as the card names them. */
export const CARD_VISITS: { visit: number; bn: string; en: string; ageBn: string; ageEn: string }[] = [
  { visit: 1, bn: '১ম বার',  en: '1st visit', ageBn: '৬ সপ্তাহ',  ageEn: '6 weeks' },
  { visit: 2, bn: '২য় বার',  en: '2nd visit', ageBn: '১০ সপ্তাহ', ageEn: '10 weeks' },
  { visit: 3, bn: '৩য় বার',  en: '3rd visit', ageBn: '১৪ সপ্তাহ', ageEn: '14 weeks' },
  { visit: 4, bn: '৪র্থ বার', en: '4th visit', ageBn: '১৮ সপ্তাহ', ageEn: '18 weeks' },
  { visit: 5, bn: '৫ম বার',  en: '5th visit', ageBn: '৯ মাস',     ageEn: '9 months' },
  { visit: 6, bn: '৬ষ্ঠ বার', en: '6th visit', ageBn: '১৫ মাস',    ageEn: '15 months' }
];

export const SCHEDULE_BY_KEY: Readonly<Record<string, ScheduleEntry>> =
  Object.fromEntries(SCHEDULE.map(s => [s.key, s]));

/** Minimum days between consecutive doses of the same antigen series. */
export const MIN_INTERVAL_DAYS = 28;

/** Grace period before a dose that is merely "due" is reported as overdue. */
export const OVERDUE_GRACE_DAYS = 14;

/** Days a dose may precede its scheduled minimum age before it is flagged. */
export const EARLY_TOLERANCE_DAYS = 4;

/**
 * Whether a string is a real calendar date in strict ISO form.
 *
 * Every other function in this module assumes its input passed this. Before it
 * existed, "15/03/2026" parsed to an Invalid Date, every comparison against NaN
 * came out false, and a record with an unreadable date sailed through every
 * interval, age and future-date check without raising a single flag. A date the
 * rules cannot read is now itself a blocking conflict.
 *
 * The round trip rejects dates JavaScript would otherwise silently roll forward:
 * "2023-02-29" and "2024-02-30" both become 1 March if you let them.
 */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export function parseDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((parseDate(toIso).getTime() - parseDate(fromIso).getTime()) / 86_400_000);
}

export function addDays(iso: string, n: number): string {
  const d = parseDate(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function ageInDays(dob: string, asOf: string): number {
  return daysBetween(dob, asOf);
}

export function ageLabel(dob: string, asOf: string): string {
  const days = ageInDays(dob, asOf);
  if (days < 0) return '—';
  const months = Math.floor(days / 30.44);
  if (months < 24) return `${months} mo`;
  return `${Math.floor(months / 12)}y ${months % 12}mo`;
}

export interface DoseInput {
  antigen: string;
  givenOn: string;
}

/** Scheduled doses the child is eligible for but has no record of. */
export function outstandingDoses(dob: string, doses: DoseInput[], asOf: string): ScheduleEntry[] {
  if (!isIsoDate(dob)) return [];
  const age = ageInDays(dob, asOf);
  const given = new Set(doses.filter(d => isIsoDate(d.givenOn)).map(d => d.antigen));
  return SCHEDULE.filter(s => age >= s.dueDays && !given.has(s.key));
}

/** Outstanding doses past the grace period — a defaulter signal. */
export function overdueDoses(dob: string, doses: DoseInput[], asOf: string): ScheduleEntry[] {
  if (!isIsoDate(dob)) return [];
  const age = ageInDays(dob, asOf);
  const given = new Set(doses.filter(d => isIsoDate(d.givenOn)).map(d => d.antigen));
  return SCHEDULE.filter(s => age >= s.dueDays + OVERDUE_GRACE_DAYS && !given.has(s.key));
}

/**
 * Doses that may be administered at today's session.
 *
 * Uses the same early tolerance the validator does — offering a dose the
 * validator would then flag as early is the tool contradicting itself — and
 * respects the minimum interval, so it never offers Penta-2 ten days after
 * Penta-1.
 */
export function dueNow(dob: string, doses: DoseInput[], asOf: string): ScheduleEntry[] {
  if (!isIsoDate(dob) || !isIsoDate(asOf)) return [];
  const age = ageInDays(dob, asOf);
  const valid = doses.filter(d => isIsoDate(d.givenOn));
  const given = new Set(valid.map(d => d.antigen));
  return SCHEDULE.filter(s => {
    if (given.has(s.key)) return false;
    // Same rule as the validator: minimum age for first doses and single-dose
    // vaccines, minimum interval for everything after. A later dose is still
    // not OFFERED before its scheduled age — that is a matter of following the
    // schedule, not of validity.
    if (age < s.dueDays - EARLY_TOLERANCE_DAYS) return false;
    if (!s.series || s.n === 1) return true;
    const previous = SCHEDULE.find(p => p.series === s.series && p.n === s.n - 1);
    const prevDose = previous && valid.find(d => d.antigen === previous.key);
    // The previous dose in the series must have been given, at least the minimum
    // interval ago.
    return !!prevDose && daysBetween(prevDose.givenOn, asOf) >= MIN_INTERVAL_DAYS;
  });
}

export function scheduledDateFor(dob: string, antigen: string): string | null {
  const entry = SCHEDULE_BY_KEY[antigen];
  return entry ? addDays(dob, entry.dueDays) : null;
}
